import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import clsx from "clsx";

import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContentText from "@material-ui/core/DialogContentText";
import Dialog from "src/components/Dialog";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import MuiAlert from "@material-ui/lab/Alert";

import { createStyles, makeStyles } from "@material-ui/core/styles";
import type { Theme } from "src/theme";

import { loadNewSyncSales } from "src/store/sale/actions";
import { setActiveDialog } from "src/store/adminHtml/actions";
import { markErrorAlertViewed } from "src/apiService";
import type {
  Alert,
  AlertEBay,
  AlertAmazon,
  AlertEtsy,
  AlertEtsyNoShop,
  AlertPoshmark,
  AlertPlaidError,
} from "src/interfaces/alert.interface";
import { getEBayOAuthUrl } from "src/apiService/modules/ebay";
import { getAmazonOAuthUrl } from "src/apiService/modules/amazon";
import {
  etsyRequestAuthorizationCode,
  etsyDeleteIntegration,
} from "src/apiService/modules/etsy";
import { useLoadingBackdrop } from "src/contexts/LoadingBackdropContext";
import {
  poshmarkIntegrationUpdate,
  poshmarkIntegrationRemoveErrorFlag,
} from "src/apiService/modules/poshmark";
import { getExtensionId, MissingExtensionError } from "src/utils/extension";
import { openZendeskWithSSO } from "src/utils/zendeskSso";
import {
  getPoshmarkIntegrations,
  getEtsyIntegrations,
} from "src/store/plaidIntegration/selector";
import { bankAccountByIntegrationIdSelector } from "src/store/bankAccount/selector";
import { getPoshmarkUser } from "src/utils/extension/poshmark";
import PoshmarkClient, {
  NotLoggedError,
} from "src/store/integrationSync/actions/poshmark/PoshmarkClient";
import UpdateIntegrationAccountDialog from "src/pages/RootDialogs/UpdateIntegrationAccountDialog";

import WalkThrough from "./WalkThroughNotificationsDialog";
import getAlertSelector from "./getAlertSelector";
import Edit, {
  type DialogState as SaleEditDialog,
} from "src/pages/TransactionDetails/Sales/Edit";

export interface NotificationsDialogProps {
  open: boolean;
  onClose: () => void;
  items: Alert[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    selectedRow: {
      backgroundColor: theme.palette.action.selected,
    },
    notificationItem: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      marginBottom: theme.spacing(1),
      minHeight: 60,
      transition: "background-color 0.2s ease, transform 0.2s ease",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
        cursor: "pointer",
        transform: "translateX(4px)",
      },
    },
  })
);

function EBayRefreshTokenDialogContent({ integrationId }) {
  useEffect(() => {
    if (!integrationId) return;
    let cancel = false;

    (async () => {
      const params: { integrationId: string; integrationName?: string } = {
        integrationId,
      };

      if (integrationId === "ebay") params.integrationName = "eBay";
      const {
        data: { url },
      } = await getEBayOAuthUrl(params);

      if (cancel) return;

      (window.location as any) = url;
    })();

    return () => {
      cancel = true;
    };
  }, [integrationId]);

  return (
    <DialogContent>
      <Box display="flex" justifyContent="center" p={1}>
        <CircularProgress />
      </Box>
    </DialogContent>
  );
}

function EBayRefreshTokenDialog({
  integrationId,
  onClose,
}: {
  integrationId?: string;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!integrationId} onClose={onClose}>
      <DialogTitle>Refresh eBay integration</DialogTitle>
      <EBayRefreshTokenDialogContent integrationId={integrationId} />
    </Dialog>
  );
}
function AmazonRefreshTokenDialogContent({ integrationId }) {
  useEffect(() => {
    if (!integrationId) return;
    let cancel = false;

    (async () => {
      const authResponse = await getAmazonOAuthUrl();

      if (cancel) return;
      window.location.href = authResponse.data.url;
    })();

    return () => {
      cancel = true;
    };
  }, [integrationId]);

  return (
    <DialogContent>
      <Box display="flex" justifyContent="center" p={1}>
        <CircularProgress />
      </Box>
    </DialogContent>
  );
}

function AmazonRefreshTokenDialog({
  integrationId,
  onClose,
}: {
  integrationId?: string;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!integrationId} onClose={onClose}>
      <DialogTitle>Refresh Amazon integration</DialogTitle>
      <AmazonRefreshTokenDialogContent integrationId={integrationId} />
    </Dialog>
  );
}

function EtsyRefreshTokenDialogContent({ integrationId }) {
  const [error, setError] = useState<string>();
  const backdrop = useLoadingBackdrop();

  useEffect(() => {
    if (!integrationId) return;
    let cancel = false;

    (async () => {
      try {
        // Refresh the existing integration so the bank/GL accounts are kept;
        // the user just re-authenticates with Etsy.
        const authResponse = await etsyRequestAuthorizationCode({
          refresh_integration_id: integrationId,
        });

        if (cancel) return;
        // This dialog's own spinner covered the request; the backdrop covers
        // the browser navigation that follows, which outlives the dialog.
        backdrop.current.show();
        window.location.href = authResponse.data.etsyURL;
      } catch (e) {
        if (cancel) return;
        console.error("Failed to start Etsy reconnect", e);
        setError(
          "We couldn't start the Etsy reconnect. Please try again or contact support."
        );
      }
    })();

    return () => {
      cancel = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- backdrop is a stable ref; listing it would risk restarting the redirect
  }, [integrationId]);

  return (
    <DialogContent>
      {error ? (
        <Box mb={2}>
          <MuiAlert severity="error">{error}</MuiAlert>
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" p={1}>
          <CircularProgress />
        </Box>
      )}
    </DialogContent>
  );
}

function EtsyRefreshTokenDialog({
  integrationId,
  onClose,
}: {
  integrationId?: string;
  onClose: () => void;
}) {
  return (
    <Dialog open={!!integrationId} onClose={onClose}>
      <DialogTitle>Refresh Etsy integration</DialogTitle>
      <EtsyRefreshTokenDialogContent integrationId={integrationId} />
    </Dialog>
  );
}

function PoshmarkRefreshTokenDialogContent({ onClose, integration }) {
  const [error, setError] = useState<string>();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!integration) return;
    let cancel = false;

    (async () => {
      let extensionId;

      try {
        try {
          extensionId = getExtensionId();
        } catch (e) {
          if (e instanceof MissingExtensionError) {
            setError(
              "Looks like you don’t have the My Reseller Genie extension installed. Click profile icon > Web Extension and download the extension in order to connect your account."
            );
            return;
          }

          throw e;
        }

        if (!extensionId) {
          setError(
            "Looks like you don’t have the My Reseller Genie extension installed. Click profile icon > Web Extension and download the extension in order to connect your account."
          );
          return;
        }

        const client = new PoshmarkClient();
        let cookieUserId;
        try {
          cookieUserId = await client.getUserId();
        } catch (e) {
          if (e instanceof NotLoggedError) {
            setError("You must be logged in to poshmark");
            return;
          }

          throw e;
        }

        if (cancel) return;

        if (cookieUserId !== integration.uid) {
          setError("You must be logged in to poshmark with the correct user");
          return;
        }

        const data = await getPoshmarkUser();
        if (cancel) return;

        if (data.cookies?.length) {
          await poshmarkIntegrationUpdate({
            integrationId: integration.id,
            cookies: data.cookies,
          });
          if (cancel) return;
          await poshmarkIntegrationRemoveErrorFlag(integration.id);

          if (cancel) return;
          onCloseRef.current();
        }
      } catch (e) {
        setError((e as Error).message);
        return;
      }
    })();

    return () => {
      cancel = true;
    };
  }, [integration]);

  return (
    <DialogContent>
      {error ? (
        <Box mb={2}>
          <MuiAlert severity="error">{error}</MuiAlert>
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" p={1}>
          <CircularProgress />
        </Box>
      )}
    </DialogContent>
  );
}

function PoshmarkRefreshTokenDialog({
  integrationId,
  onClose,
}: {
  integrationId?: string;
  onClose: () => void;
}) {
  const integration = useSelector((state: any) => {
    const integrations = getPoshmarkIntegrations(state);
    return integrations.find(({ id }) => id === integrationId);
  });
  return (
    <Dialog open={!!integrationId} onClose={onClose}>
      <DialogTitle>Refresh Poshmark integration</DialogTitle>
      <PoshmarkRefreshTokenDialogContent
        integration={integration}
        onClose={onClose}
      />
    </Dialog>
  );
}

function NotificationsDialogContent({
  onClose,
  items,
}: Omit<NotificationsDialogProps, "open">) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const backdrop = useLoadingBackdrop();
  const [alertDialog, setAlertDialog] = useState<
    | {
        type: "ebay" | "amazon" | "etsy" | "poshmark" | "plaid";
        integrationId: string;
      }
    | { type: "edit-sale"; dialog: SaleEditDialog }
  >();
  // Tracks the Etsy reconnect dialog. When set, the dialog is open and carries
  // the bank account that was linked to the errored integration so we can reuse
  // it on reconnect instead of creating duplicate bank/GL accounts.
  const [etsyReconnect, setEtsyReconnect] = useState<{
    open: true;
    bankAccountId?: string;
  }>();

  // A user can only have one Etsy integration. Resolve its currently linked
  // bank account so we can reattach it after deleting the errored integration.
  const etsyIntegrationId = useSelector(
    (state: any) => getEtsyIntegrations(state)?.[0]?.id
  );
  const etsyBankAccountId = useSelector((state: any) =>
    etsyIntegrationId
      ? bankAccountByIntegrationIdSelector(state, etsyIntegrationId)?.id
      : undefined
  );

  const handleEtsyNoShop = async (item: AlertEtsyNoShop) => {
    // Capture the linked bank account before deleting the integration, since
    // deleting detaches (orphans) the bank account.
    const bankAccountId = etsyBankAccountId;
    try {
      await etsyDeleteIntegration();
      await markErrorAlertViewed(item.id);
    } catch (e) {
      console.error("Failed to reset Etsy integration after no-shop error", e);
    }

    setEtsyReconnect({ open: true, bankAccountId });
  };

  // Etsy OAuth has no way to force account selection, so we send the user to
  // Etsy's logout first. Opens in a new tab so this dialog stays available.
  const handleEtsySignOut = () => {
    window.open("https://www.etsy.com/", "_blank")?.focus();
  };

  // Kick off the Etsy auth flow. A full-page redirect is used so it can't be
  // cancelled by a re-render.
  const handleEtsyConnect = async () => {
    try {
      const authResponse = await etsyRequestAuthorizationCode(
        etsyReconnect?.bankAccountId
          ? { bankAccountId: etsyReconnect.bankAccountId }
          : undefined
      );
      backdrop.current.show();
      window.location.href = authResponse.data.etsyURL;
    } catch (e) {
      console.error("Failed to start Etsy reconnect", e);
    }
  };

  const handleClick = (item: Alert) => {
    switch (item.type) {
      case "plaid_error":
        setAlertDialog({
          type: "plaid",
          integrationId: (item as AlertPlaidError).integrationId,
        });
        break;

      case "error":
        markErrorAlertViewed(item.id);
        onClose();
        break;

      case "error-link":
        openZendeskWithSSO(item.href);
        markErrorAlertViewed(item.id);
        onClose();
        break;

      case "etsy-no-shop-error":
        handleEtsyNoShop(item);
        break;

      case "returns":
        dispatch(setActiveDialog("returns_review"));
        onClose();
        break;

      case "inventory":
        dispatch(setActiveDialog("inventory_sync"));
        onClose();
        break;

      case "sales":
        dispatch(loadNewSyncSales());
        dispatch(setActiveDialog("match_sales"));
        onClose();
        break;

      case "review_sales":
        dispatch(setActiveDialog("review_sales"));
        onClose();
        break;

      case "expenses":
        dispatch(setActiveDialog("categorize_expenses"));
        onClose();
        break;

      case "ebay_error":
        setAlertDialog({
          type: "ebay",
          integrationId: (item as AlertEBay).integrationId,
        });
        break;

      case "amazon_error":
        setAlertDialog({
          type: "amazon",
          integrationId: (item as AlertAmazon).integrationId,
        });
        break;

      case "etsy_error":
        setAlertDialog({
          type: "etsy",
          integrationId: (item as AlertEtsy).integrationId,
        });
        break;

      case "poshmark_error":
        setAlertDialog({
          type: "poshmark",
          integrationId: (item as AlertPoshmark).integrationId,
        });
        break;

      case "poshmark_deductions":
        dispatch(setActiveDialog("poshmark_deductions"));
        break;

      case "etsy-incorrect-total":
        setAlertDialog({
          type: "edit-sale",
          dialog: { type: "edit", id: item.docId },
        });
        break;

      case "etsy-incorrect-refund-total":
        setAlertDialog({
          type: "edit-sale",
          dialog: { type: "edit", id: item.docId },
        });
        break;

      default:
        console.warn("Unhandled type", (item as any).type, item);
        break;
    }
  };

  return (
    <>
      <DialogTitle>Notifications</DialogTitle>
      <DialogContent>
        {items.length ? (
          <List>
            {items.map((item) => (
              <ListItem
                key={JSON.stringify(item)}
                onClick={() => handleClick(item)}
                className={clsx(
                  classes.notificationItem,
                  getAlertSelector(item.type)
                )}
              >
                {item.message}
              </ListItem>
            ))}
          </List>
        ) : (
          <DialogContentText>No current notifications.</DialogContentText>
        )}
      </DialogContent>
      <EBayRefreshTokenDialog
        integrationId={
          alertDialog?.type === "ebay" ? alertDialog.integrationId : undefined
        }
        onClose={() => setAlertDialog(undefined)}
      />
      <AmazonRefreshTokenDialog
        integrationId={
          alertDialog?.type === "amazon" ? alertDialog.integrationId : undefined
        }
        onClose={() => setAlertDialog(undefined)}
      />
      <EtsyRefreshTokenDialog
        integrationId={
          alertDialog?.type === "etsy" ? alertDialog.integrationId : undefined
        }
        onClose={() => setAlertDialog(undefined)}
      />
      <PoshmarkRefreshTokenDialog
        integrationId={
          alertDialog?.type === "poshmark"
            ? alertDialog.integrationId
            : undefined
        }
        onClose={() => setAlertDialog(undefined)}
      />
      <UpdateIntegrationAccountDialog
        integrationId={
          alertDialog?.type === "plaid" ? alertDialog.integrationId : undefined
        }
        onClose={() => setAlertDialog(undefined)}
      />
      <Edit
        value={
          alertDialog?.type === "edit-sale" ? alertDialog.dialog : undefined
        }
        onChange={(dialog) => {
          setAlertDialog(dialog ? { type: "edit-sale", dialog } : undefined);
        }}
      />
      <Dialog
        open={!!etsyReconnect?.open}
        onClose={() => setEtsyReconnect(undefined)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Connect your Etsy seller account</DialogTitle>
        <DialogContent>
          <DialogContentText>
            To connect to the right Etsy account, first sign out of Etsy.
          </DialogContentText>
          <Box my={2} display="flex" justifyContent="center">
            <Button
              variant="contained"
              color="primary"
              onClick={handleEtsySignOut}
            >
              Sign Out of Etsy
            </Button>
          </Box>
          <DialogContentText>
            Once you have signed out of Etsy, click the button below to
            reconnect and sign into your Etsy selling account.
          </DialogContentText>
          <Box mt={2} display="flex" justifyContent="center">
            <Button
              variant="contained"
              color="primary"
              onClick={handleEtsyConnect}
            >
              Connect Seller Account
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

function NotificationsDialog({
  open,
  onClose,
  items,
}: NotificationsDialogProps) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open) {
      setReady(false);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll={"body"}
      fullWidth={true}
      maxWidth={"sm"}
      TransitionProps={{ onEntered: () => setReady(true) }}
    >
      <NotificationsDialogContent onClose={onClose} items={items} />
      <WalkThrough alerts={items} ready={ready} />
    </Dialog>
  );
}

export default NotificationsDialog;
